import { z } from "zod";

/**
 * Sign-in on ESI's own page and the signed-in user's account settings (openspec change
 * account-and-sign-in). Every credential goes to the API only; the browser never talks to the
 * authorization server's sign-in APIs and never holds one of its tokens.
 */

/** The user pool's password policy (infra/lib/identity.ts): at least 12 characters. */
export const PASSWORD_MIN_LENGTH = 12;
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(256);
/** A TOTP code: six digits, spaces tolerated (authenticator apps group them). */
export const totpCodeSchema = z
  .string()
  .transform((v) => v.replace(/\s+/g, ""))
  .pipe(z.string().regex(/^\d{6}$/));

/** A WebAuthn ceremony's options or result, relayed as JSON between browser and server. */
export const webAuthnJsonSchema = z.record(z.string(), z.unknown());

/**
 * How the email step may answer (#419, D6, Q3 as amended): absent, today's answers (a release
 * before `choice` sends none); `choice`, the choice step on a domain whose mode allows ESI
 * credentials; `credentials`, the person picked "Use password or passkey" there. A domain whose
 * mode allows none answers as without it.
 */
export const SIGN_IN_START_METHODS = ["choice", "credentials"] as const;
export const signInStartMethodSchema = z.enum(SIGN_IN_START_METHODS);

export const signInStartRequestSchema = z.object({
  email: z.email().max(320),
  returnTo: z.string().max(2048).optional(),
  method: signInStartMethodSchema.optional(),
});
export const signInPasswordRequestSchema = z.object({ password: z.string().min(1).max(256) });
export const signInChallengeRequestSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("totp"), code: totpCodeSchema }),
  /** Operator accounts an AWS administrator created or reset: the first password, set here. */
  z.object({ type: z.literal("new_password"), password: passwordSchema }),
]);
export const passkeyAssertionRequestSchema = z.object({ credential: webAuthnJsonSchema });
export const totpVerifyRequestSchema = z.object({ code: totpCodeSchema });

/** Second factors a user can enrol at sign-in or in settings. */
export const MFA_METHODS = ["totp", "passkey"] as const;
export type MfaMethod = (typeof MFA_METHODS)[number];

/**
 * The next step of a sign-in. The same answer for an unknown, a disabled and an existing
 * native email (enumeration safety): `credentials` never says whether the account exists.
 */
export const signInStepSchema = z.discriminatedUnion("next", [
  /** A verified domain routes to its tenant's IdP: the browser navigates there. */
  z.object({ next: z.literal("redirect"), url: z.string() }),
  /**
   * A verified domain whose mode allows ESI credentials (#419, D6): "Continue with
   * `displayName`" (`/start` again without `method`) or "Use password or passkey" (`/start` with
   * `method: "credentials"`). The same for every e-mail of the domain.
   */
  z.object({ next: z.literal("choice"), providerName: z.string(), displayName: z.string() }),
  z.object({ next: z.literal("credentials"), passkey: z.boolean() }),
  z.object({ next: z.literal("totp") }),
  z.object({ next: z.literal("new_password") }),
  /** A passkey ceremony: `options` go to `navigator.credentials.get`. */
  z.object({ next: z.literal("passkey"), options: webAuthnJsonSchema }),
  /** MFA is required and the account has no second factor yet: no session before enrolment. */
  z.object({ next: z.literal("enrol"), methods: z.array(z.enum(MFA_METHODS)) }),
  z.object({ next: z.literal("done"), returnTo: z.string() }),
]);
export type SignInStep = z.infer<typeof signInStepSchema>;

/** Pre-session flows answer with these codes; the message is the SPA's. */
export const AUTH_ERROR_CODES = [
  /** Wrong email, password, code or passkey, or no such account: never told apart. */
  "invalid_credentials",
  /** Too many attempts: `retryAfter` seconds, also in `Retry-After`. */
  "throttled",
  /** No flow bound to this browser, or it expired: start again from the email. */
  "flow_expired",
  /** An invitation or reset link that is unknown, used or expired. */
  "invalid_link",
  /**
   * An invitation link for an account that already has a password: the link is spent and sets
   * nothing (sign in, or reset the password).
   */
  "password_already_set",
  /** The authorization server's password policy refused the new password. */
  "weak_password",
  /** A wrong TOTP code while enrolling (the step stays open). */
  "invalid_code",
] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];
export const authErrorSchema = z.object({
  code: z.enum(AUTH_ERROR_CODES),
  retryAfter: z.number().int().optional(),
});
export type AuthError = z.infer<typeof authErrorSchema>;

/** `POST /api/auth/csrf`: a pre-session synchroniser token bound to this browser. */
export const preSessionCsrfResponseSchema = z.object({ csrfToken: z.string() });

/** Invitation and reset links carry the token in the URL fragment, never in the query. */
export const accountTokenRequestSchema = z.object({ token: z.string().min(16).max(128) });
export const setPasswordRequestSchema = accountTokenRequestSchema.extend({
  password: passwordSchema,
});
export const emailRequestSchema = z.object({ email: z.email().max(320) });
export const accountLinkSchema = z.object({ valid: z.boolean() });
/**
 * `POST /api/auth/invitation/check`: a valid invitation also says which organisation, for which
 * email and from whom (ui-refresh-auth-console, slice 1b); any other answer is `{ valid: false }`
 * alone. Optional: an older API answers `valid` only.
 */
export const invitationCheckSchema = accountLinkSchema.extend({
  tenantName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  invitedBy: z.string().nullable().optional(),
});
export type InvitationCheck = z.infer<typeof invitationCheckSchema>;
/** Reset and invitation-resend requests: always this, whatever the email. */
export const linkSentSchema = z.object({ sent: z.literal(true) });

/** Authenticator enrolment: the shared secret as text and as an `otpauth://` URI (QR code). */
export const totpSetupSchema = z.object({ secret: z.string(), uri: z.string() });

// ── Account settings ────────────────────────────────────────────────────────────────────

export const LANGUAGES = ["en", "fr"] as const;
export const THEMES = ["system", "light", "dark"] as const;
export const preferencesSchema = z.object({
  language: z.enum(LANGUAGES).nullable(),
  theme: z.enum(THEMES).nullable(),
  /**
   * "Suggest feedback" (feedback-from-conversation D6): false stops the agent's unprompted
   * feedback suggestions for this person; null is the default (on). Absent from older APIs.
   */
  suggestFeedback: z.boolean().nullable().optional(),
});
export type Preferences = z.infer<typeof preferencesSchema>;
export const preferencesUpdateSchema = preferencesSchema.partial();

export const displayNameSchema = z.string().trim().min(1).max(100);
export const profileUpdateSchema = z.object({ displayName: displayNameSchema });

/** Control characters and bidirectional overrides (spoofing in lists and the audit). */
const NAME_FORBIDDEN = /[\p{Cc}\u2028\u2029\u202A-\u202E\u2066-\u2069]/u;
/** At most this many characters in an operator's display name (#391). */
export const OPERATOR_NAME_MAX = 80;
/**
 * An operator's display name (#391), shown wherever the console names operators: trimmed, 1 to
 * {@link OPERATOR_NAME_MAX} characters once runs of white space (line breaks included) collapse to
 * one space, at least one letter or digit, and no control character or bidirectional override.
 */
export const operatorDisplayNameSchema = z
  .string()
  .transform((v) => v.trim().replace(/\s+/gu, " "))
  .pipe(
    z
      .string()
      .min(1)
      .max(OPERATOR_NAME_MAX)
      .refine((v) => !NAME_FORBIDDEN.test(v), { message: "invalid_characters" })
      .refine((v) => /[\p{L}\p{N}]/u.test(v), { message: "needs_letter" }),
  );
export const operatorProfileUpdateSchema = z.object({ name: operatorDisplayNameSchema });
export type OperatorProfileUpdate = z.infer<typeof operatorProfileUpdateSchema>;

export const passkeySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
});
export type Passkey = z.infer<typeof passkeySchema>;
/**
 * The console operator's own passkeys (operator-identity-pool 7.2): listed only while the code
 * step-up's sealed token lives (90 s after it; `passkeys` null before and after).
 */
export const operatorPasskeysResponseSchema = z.object({
  available: z.boolean(),
  passkeys: z.array(passkeySchema).nullable(),
});
export type OperatorPasskeysResponse = z.infer<typeof operatorPasskeysResponseSchema>;
/** The authenticator names the passkey (Cognito keeps its friendly name). */
export const passkeyRegistrationRequestSchema = z.object({ credential: webAuthnJsonSchema });

/** A person's ESI credential beside their SSO (#419): held, and allowed by the domain now. */
export const esiCredentialStateSchema = z.object({
  held: z.boolean(),
  allowed: z.boolean(),
  /**
   * A set-password link was sent and never used (the account it sets is not attached yet): a
   * tenant admin may revoke it. The admin's user detail only; absent elsewhere and from older
   * releases.
   */
  pending: z.boolean().optional(),
});
export type EsiCredentialState = z.infer<typeof esiCredentialStateSchema>;

/** `GET /api/account`: what the account page shows; no credential material. */
export const accountResponseSchema = z.object({
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  /** Federated accounts: the name, password and MFA belong to the company IdP. */
  federated: z.boolean(),
  preferences: preferencesSchema,
  security: z.object({
    /** `auth.mfa` resolves to `required` for this user (or they are an operator). */
    mfaRequired: z.boolean(),
    totp: z.boolean(),
    /** Passkeys are offered on this deployment (a custom domain is the relying party). */
    passkeysAvailable: z.boolean(),
    /**
     * Listing passkeys needs the user's own token at the authorization server: null until the
     * session re-authenticated (then the list is shown and can be managed).
     */
    passkeys: z.array(passkeySchema).nullable(),
  }),
  /** The current session proved its credentials within the last 5 minutes. */
  recentlyAuthenticated: z.boolean(),
  /**
   * An ESI credential (password or passkey) next to the person's SSO (#419): whether they hold
   * one and whether their domain's sign-in mode allows it. Absent from older releases.
   */
  esiCredential: esiCredentialStateSchema.optional(),
});
export type AccountResponse = z.infer<typeof accountResponseSchema>;

/** Where the set-password link of an ESI credential went (the person's own e-mail). */
export const esiCredentialLinkSentSchema = z.object({ sentTo: z.string() });
export type EsiCredentialLinkSent = z.infer<typeof esiCredentialLinkSentSchema>;

export const reauthPasswordRequestSchema = z.object({
  password: z.string().min(1).max(256),
  code: totpCodeSchema.optional(),
});
/** A re-authentication's outcome: done, or the TOTP code is needed too. */
export const reauthResultSchema = z.object({ next: z.enum(["done", "totp"]) });
export const passwordChangeRequestSchema = z.object({ password: passwordSchema });

export const accountSessionSchema = z.object({
  id: z.string(),
  current: z.boolean(),
  /** Parsed from the user agent, e.g. "Firefox on macOS"; null when unknown. */
  device: z.string().nullable(),
  /** The sign-in network, truncated (/24 or /48). */
  network: z.string().nullable(),
  signedInAt: z.string(),
  lastSeenAt: z.string(),
});
export type AccountSession = z.infer<typeof accountSessionSchema>;
